import { Dispatch } from '@reduxjs/toolkit'
import { UserSession } from 'api/Identity/Types/UserSession'
import { OutstandingUserTicketsSummaryDto } from 'api/Tickets/Types/OutstandingUserTicketsSummaryDto'
import { TicketStatuses } from 'api/Tickets/Types/TicketStatus'
import { errorToast, successToast, Toast } from 'components/Toast/Toast'
import { TicketsViewType } from 'routes/Tickets/TicketsViewType'
import { isEmpty } from 'utils/is-empty'

import { TicketsApiClient } from '../../utils/api/ticketsApi'
import { ticketsSlice } from './tickets.slice'

export const completeTicketsAction = (
  ticketsApiClient: TicketsApiClient,
  ids: string[],
  viewTypeId: TicketsViewType,
  setToast: (toast: Toast) => void,
  forceRefresh?: () => void
) => {
  return async (): Promise<void> => {
    if (isEmpty(ids)) {
      return setToast(errorToast('Cannot complete ticket with status Closed.'))
    }
    try {
      await ticketsApiClient.completeTickets(ids, viewTypeId)
      setToast({ type: 'success', title: 'Tickets completed successfully.' })
      if (forceRefresh) forceRefresh()
    } catch (e) {
      console.error('Cannot complete tickets.', e)
    }
  }
}

export const completeClientTicketsAction = (
  ticketsApiClient: TicketsApiClient,
  user: UserSession,
  ids: string[],
  viewTypeId: TicketsViewType,
  setToast: (toast: Toast) => void
) => {
  return async (): Promise<void> => {
    if (isEmpty(ids)) {
      return setToast(errorToast('Cannot complete ticket with status Closed.'))
    }
    try {
      if (user) {
        await ticketsApiClient.completeClientTickets(ids, viewTypeId)
        setToast(successToast('Tickets completed successfully'))
      }
    } catch (error) {
      console.error('Cannot complete tickets.', error)
    }
  }
}

export const acquireClientTicketsAction = (
  ticketsApiClient: TicketsApiClient,
  user: UserSession,
  ticketIds: string[],
  viewTypeId: TicketsViewType,
  setToast: (toast: Toast) => void
) => {
  return async (): Promise<void> => {
    if (isEmpty(ticketIds)) {
      return setToast(errorToast('Cannot acquire ticket with status: CLOSED'))
    }
    try {
      await ticketsApiClient.acquireClientTickets({
        ticketIds,
        viewTypeId,
        assigneeId: user.id,
      })
      setToast(successToast('Tickets acquired successfully'))
      getAndSetTicketsAssignedToUserSession(ticketsApiClient)
    } catch (e) {
      console.error('Cannot complete tickets.', e)
    }
  }
}

export const undoAcquireClientTicketsAction = (
  ticketsApiClient: TicketsApiClient,
  ticketIds: string[],
  viewTypeId: TicketsViewType,
  setToast: (toast: Toast) => void
) => {
  return async (): Promise<void> => {
    if (isEmpty(ticketIds)) {
      return setToast(errorToast(`Cannot acquire ticket with status: ${TicketStatuses.CLOSED}`))
    }
    try {
      await ticketsApiClient.acquireClientTickets({
        ticketIds,
        viewTypeId,
      })
      setToast(successToast(`Tickets returned successfully`))
      getAndSetTicketsAssignedToUserSession(ticketsApiClient)
    } catch (e) {
      console.error('Cannot complete tickets.', e)
    }
  }
}

export const assignTicketsAction = (
  ticketsApiClient: TicketsApiClient,
  assigneeId: string,
  ids: string[],
  onFilterFetch: () => void,
  setToast: (toast: Toast) => void
) => {
  return async (): Promise<void> => {
    if (isEmpty(ids)) {
      return setToast(errorToast('Closed tickets cannot be assigned.'))
    }
    await ticketsApiClient.assigneeClientToTickets({
      assigneeId,
      ticketIds: ids,
    })
    setToast(successToast('Tickets assigned successfully'))
    await onFilterFetch()
  }
}

export const unAssignTicketsAction = (
  ticketsApiClient: TicketsApiClient,
  ticketIds: string[],
  onFilterFetch: () => void,
  setToast: (toast: Toast) => void
) => {
  return async (): Promise<void> => {
    if (isEmpty(ticketIds)) {
      return setToast(errorToast(`Closed tickets cannot be assigned`))
    }
    await ticketsApiClient.assigneeClientToTickets({
      ticketIds,
    })
    setToast(successToast('Tickets unassigned successfully.'))
    await onFilterFetch()
  }
}

export const processPaymentTicketsAction = (
  ticketsApiClient: TicketsApiClient,
  transactionIds: string[],
  onFilterFetch: () => void,
  setToast: (toast: Toast) => void
) => {
  return async (): Promise<void> => {
    if (isEmpty(transactionIds)) {
      return setToast(errorToast(`Closed tickets cannot be processed`))
    }
    await ticketsApiClient.processPaymentTickets({
      transactionIds,
    })
    setToast(
      successToast('Process has now started. Results will appear once process is completed.')
    )
    await onFilterFetch()
  }
}

export const getTicketStatusTypeAction = (ticketsApiClient: TicketsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await ticketsApiClient.getTicketStatuses()
      dispatch(ticketsSlice.actions.getTicketStatus(data))
    } catch (e) {
      console.error('Cannot get ticket statuses', e)
    }
  }
}

export const getMlStatusesAction = (ticketsApiClient: TicketsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await ticketsApiClient.getMlStatuses()
      dispatch(ticketsSlice.actions.getMlStatuses(data))
    } catch (e) {
      console.error('Cannot get ML statuses', e)
    }
  }
}

export const getTicketTypeAction = (viewType: number, ticketsApiClient: TicketsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await ticketsApiClient.getTicketTypes(viewType)
      dispatch(ticketsSlice.actions.getTicketType(data))
    } catch (e) {
      console.error('Cannot get ticket statuses', e)
    }
  }
}

export const getMyTicketViewTypesAction = (ticketsApiClient: TicketsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await ticketsApiClient.getMyTicketViewTypes()
      dispatch(ticketsSlice.actions.getMyTicketViewTypes(data))
    } catch (e) {
      console.log(`Can't get my ticket view types`, e)
    }
  }
}

export const getAndSetTicketsAssignedToUserSession = (ticketsApiClient: TicketsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await ticketsApiClient.getTicketsAssignedToUserSession()
      dispatch(ticketsSlice.actions.getOutstandingTicketsUserSession(data))
    } catch (e) {
      console.error('Cannot getTicketsAssignedToUserSession', e)
    }
  }
}
export const SetTicketsAssignedToUserSession = (data: OutstandingUserTicketsSummaryDto[]) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(ticketsSlice.actions.getOutstandingTicketsUserSession(data))
    } catch (e) {
      console.error('Cannot getTicketsAssignedToUserSession', e)
    }
  }
}
